import { useEffect, useState, useRef } from "react";
import {
  isValidSession,
  login,
  logout,
  resetPassword,
  sendVerificationEmail,
} from "@/services/auth";
import { AuthModel } from "pocketbase";
import { pb } from "./pocketbase";
import { AuthContext } from "@/contexts/auth-context";
import { identifyUserInPosthog } from "@/services/auth";
import { EmailVerificationBlocker } from "@/components/auth/email-verification-blocker";

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(isValidSession());
  const [user, setUser] = useState<AuthModel>(pb.authStore.record);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(
    pb.authStore.record?.verified ?? false
  );
  const initialized = useRef(false);

  // Setup auth store change listener
  useEffect(() => {
    pb.authStore.onChange(() => {
      console.log("Auth state changed");
      setIsAuthenticated(isValidSession());
      setUser(pb.authStore.record);
      setIsEmailVerified(pb.authStore.record?.verified ?? false);
    });
  }, []);

  // Set up realtime subscription to user record
  useEffect(() => {
    // Only subscribe if user is authenticated and has an ID and email is not verified
    if (!isAuthenticated || !user?.id || isEmailVerified) {
      return;
    }

    // Subscribe to user record changes
    pb.collection("users").subscribe(user.id, (e) => {
      if (e.action == "delete") {
        pb.authStore.clear();
        return;
      }
      pb.authStore.save(pb.authStore.token, e.record);
      // Update user record in state
      setUser(e.record);
      // Update email verification status if it changed
      if (e.record?.verified !== isEmailVerified) {
        setIsEmailVerified(e.record?.verified ?? false);

        // If email was just verified, reload the page to refresh the app state
        if (e.record?.verified) {
          window.location.reload();
        }
      }
    });

    // Return the unsubscribe function for cleanup
    return () => {
      pb.collection("users").unsubscribe(user.id);
    };
  }, [isAuthenticated, user?.id, isEmailVerified]);

  // PostHog identification
  useEffect(() => {
    if (!initialized.current && isAuthenticated && user?.id) {
      initialized.current = true;
      identifyUserInPosthog(user);
    }
  }, [isAuthenticated, user]);

  // Render email verification blocker if user is authenticated but email is not verified
  const shouldShowEmailVerificationBlocker =
    isAuthenticated && user?.id && !isEmailVerified;

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
        resetPassword,
        sendVerificationEmail,
        isEmailVerified,
      }}
    >
      {shouldShowEmailVerificationBlocker && <EmailVerificationBlocker />}
      <div
        className={
          shouldShowEmailVerificationBlocker
            ? "pointer-events-none blur-sm"
            : ""
        }
      >
        {children}
      </div>
    </AuthContext.Provider>
  );
};
